import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { RestApiService } from './rest-api.service';

export interface PaymentCardData {
  cardNumber: string;
  cardExpMonth: string;
  cardExpYear: string;
  cardCvv?: string;
}

export interface BillingInfo {
  firstName: string;
  lastName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

export interface PaymentRequest {
  amountBase: string;
  action: 'CHARGE' | 'REFUND';
  tokenId: string;
  billingInfo: BillingInfo;
  invoiceNumber?: string;
  note?: string;
}

export interface PaymentResponse {
  transactionId: string;
  status: string;
  amount: string;
  message?: string;
  receiptUrl?: string;
}

export interface PayJunctionTokenResponse {
  tokenId?: string;
  errors?: Array<{
    message: string;
    parameter: string;
    type: string;
  }>;
}

declare const PayJunction: (publishableKey: string) => PayJunctionSDK;

interface PayJunctionSDK {
  createToken(paymentData: PaymentCardData): Promise<PayJunctionTokenResponse>;
}

@Injectable({
  providedIn: 'root'
})
export class PayjunctionService {
  private payjunctionSDK: PayJunctionSDK | null = null;
  private publishableKey: string | null = null;

  constructor(
    private client: HttpClient,
    private restApi: RestApiService
  ) {}

  async getPublishableKey(): Promise<string> {
    if (!this.publishableKey) {
      try {
        const response = await this.client.get<{status: string, publishable_key?: string, message?: string}>(`/api/outbound/agency/${this.restApi.agencySlug()}/publishable_key`).toPromise();

        console.log('Publishable key response:', response);

        if (response.status === 'success' && response.publishable_key && response.publishable_key.trim()) {
          this.publishableKey = response.publishable_key.trim();
        } else {
          throw new Error(response.message || 'Failed to get publishable key - key is empty or missing');
        }
      } catch (error) {
        console.error('Error fetching publishable key:', error);
        throw error;
      }
    }
    return this.publishableKey;
  }

  private async initializeSDK(): Promise<PayJunctionSDK> {
    if (!this.payjunctionSDK) {
      if (typeof PayJunction !== 'undefined') {
        const key = await this.getPublishableKey();
        console.log('Initializing PayJunction SDK with key:', key ? 'key_***' + key.slice(-4) : 'MISSING');

        if (!key || typeof key !== 'string' || key.trim() === '') {
          throw new Error('Invalid publishable key: key is empty or not a string');
        }

        this.payjunctionSDK = PayJunction(key);
      } else {
        throw new Error('PayJunction SDK not loaded');
      }
    }
    return this.payjunctionSDK;
  }

  async createPaymentToken(cardData: PaymentCardData): Promise<PayJunctionTokenResponse> {
    const sdk = await this.initializeSDK();
    return await sdk.createToken(cardData);
  }

  // Payment processing is now handled by the backend using the secure token

  async createCustomer(billingInfo: BillingInfo): Promise<any> {
    return this.client
      .post(
        `/api/patient/agency/${this.restApi.agencySlug()}/payment/customer`,
        billingInfo
      )
      .toPromise();
  }

  async getPaymentHistory(): Promise<any[]> {
    return this.client
      .get<any[]>(
        `/api/patient/agency/${this.restApi.agencySlug()}/payment/history`
      )
      .toPromise();
  }

  validatePaymentAmount(amount: string): boolean {
    const numAmount = parseFloat(amount);
    return !isNaN(numAmount) && numAmount > 0;
  }

  validateCardNumber(cardNumber: string): boolean {
    const cleaned = cardNumber.replace(/\s|-/g, '');
    return /^\d{13,19}$/.test(cleaned);
  }

  validateExpiryDate(month: string, year: string): boolean {
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    if (monthNum < 1 || monthNum > 12) return false;
    if (yearNum < currentYear) return false;
    if (yearNum === currentYear && monthNum < currentMonth) return false;

    return true;
  }

  formatCardNumber(cardNumber: string): string {
    const cleaned = cardNumber.replace(/\s|-/g, '');
    return cleaned.replace(/(.{4})/g, '$1 ').trim();
  }

  maskCardNumber(cardNumber: string): string {
    const cleaned = cardNumber.replace(/\s|-/g, '');
    return '**** **** **** ' + cleaned.slice(-4);
  }
}